import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  PromotionSchedule,
  PromotionScheduleHoliday,
  PromotionScheduleTrack,
  PromotionScheduleWeekMeta,
} from '../../../../../lib/api/promotionSchedule';
import { GRAY_BAR_TONE, STATUS_STYLE, TRACK_META, TRACK_ORDER, TYPE_LABEL } from '../../constants';
import { COUPON_DETAIL_URL_PATH, EVENT_EDIT_URL_PATH } from '../../../../../constants/service/common/urlConstants';
import { setDetailPageUrl } from '../../../../../lib/common';
import {
  DAY_TYPE,
  DAY_TYPE_COLOR,
  extractScheduleMeta,
  findWeeklyShortages,
  getDayIndex,
  getDayType,
  formatCouponBenefit,
  getCouponLabel,
  getTimeRangeLabel,
  getTimelineRange,
  getWeekDates,
  getWeekdayLabel,
  groupByTrack,
  sortCouponsByBenefit,
  toDatePart,
  type WeeklyShortage,
} from '../../utils';
import { getTodayKst } from '../../utils/kst';
import { DRAG_MODE, useBarDrag, type DragMode } from '../../hooks/useBarDrag';
import { useVirtualColumns } from '../../hooks/useVirtualColumns';
import { ScheduleTooltip } from '../ScheduleTooltip';
import { ScheduleHoverCard, type ScheduleHoverCardHandle } from './ScheduleHoverCard';
import {
  BAR_HEIGHT,
  BAR_META_RESERVED,
  BAR_TWO_ROW_MIN_WIDTH,
  CHIP_WIDTH_PLAIN,
  CHIP_WIDTH_WITH_ID,
  EVENT_BADGE_WIDTH,
  TIME_BADGE_WIDTH,
  EVENT_NAME_WIDTH,
  PLAIN_META_WIDTH,
  COUPON_LIST_PADDING,
  COUPON_ROW_HEIGHT,
  DAY_COLUMN_WIDTH,
  StyledBandGrid,
  StyledBar,
  StyledCollapsedBand,
  StyledDayHeader,
  StyledDayStripe,
  StyledEmpty,
  StyledBandPlaceholder,
  StyledGanttInner,
  StyledGanttWrap,
  StyledGrid,
  StyledTodayStripe,
  StyledTrackToggle,
  StyledHeaderCell,
  StyledRow,
  StyledShortageBand,
  StyledTrackLabel,
  StyledWeekHeader,
  TRACK_LABEL_WIDTH,
} from './styles';

type Props = {
  schedules: PromotionSchedule[];
  weeks: PromotionScheduleWeekMeta[];
  holidays: PromotionScheduleHoliday[];
  /** 기간 이동 권한. 없으면 막대는 클릭만 된다 */
  canUpdate: boolean;
  onClickSchedule: (schedule: PromotionSchedule) => void;
  onMovePeriod: (params: { schedule: PromotionSchedule; startDate: string; endDate: string }) => Promise<void>;
};

/**
 * 간트 뷰 — 가로축 일자(주차 헤더로 묶음), 세로축 트랙 밴드.
 *
 * 구글시트의 정신적 모델(주차 × 트랙)을 유지해 이관 저항을 낮춘다.
 * 시트와 다른 점은 동시 진행 개수 제한이 없다는 것 — 겹치면 밴드 높이가 늘어난다.
 */
export const GanttView = (props: Props): React.JSX.Element => {
  const { schedules, weeks, holidays, canUpdate, onClickSchedule, onMovePeriod } = props;

  const range = getTimelineRange(weeks);
  const today = getTodayKst();

  /**
   * 막대·경고 라인이 공유하는 hover 카드. ref 로 조작해 **부모를 리렌더하지 않는다** —
   * hover 상태를 여기(GanttView)에 두면 마우스가 막대를 지날 때마다 수백 개 막대 트리가 재조정된다.
   */
  const hoverCardRef = useRef<ScheduleHoverCardHandle>(null);

  /**
   * 쿠폰 목록을 펼친 막대들.
   *
   * 세션 동안만 유지한다 — 새로고침하면 접힌 상태다. 기본은 밀도 우선이고,
   * 펼침은 "지금 이걸 들여다본다" 는 일시적 의도라 저장할 가치가 없다.
   */
  const [expandedCouponIds, setExpandedCouponIds] = useState<Set<number>>(new Set());

  /**
   * 접은 트랙들.
   *
   * 트랙이 6개라 세로가 길어지는데, 지금 보는 트랙만 남기면 비교가 쉬워진다.
   * 쿠폰 펼침과 같이 **세션 동안만** 유지한다 — 새로고침하면 전부 펼쳐진 상태다.
   */
  const [collapsedTracks, setCollapsedTracks] = useState<Set<PromotionScheduleTrack>>(new Set());

  const toggleTrack = useCallback((track: PromotionScheduleTrack) => {
    setCollapsedTracks((prev) => {
      const next = new Set(prev);

      if (!next.delete(track)) {
        next.add(track);
      }

      return next;
    });
  }, []);

  const toggleCouponList = useCallback((scheduleId: number) => {
    setExpandedCouponIds((prev) => {
      const next = new Set(prev);

      if (!next.delete(scheduleId)) {
        next.add(scheduleId);
      }

      return next;
    });
  }, []);

  /**
   * 막대 안의 링크·버튼이 **막대 자체의 동작을 건드리지 않게** 막는다.
   *
   * ⚠️ `stopPropagation()` 만으로는 부족하다. 막대는 `onPointerDown` 에 드래그를 걸어 두는데,
   * pointerdown 은 click 보다 먼저 온다 — 링크를 누르는 순간 막대가 드래그로 잡히고,
   * 손을 떼면 기간이 옮겨진 채 커밋된다. 그래서 **pointerdown 단계에서도** 막아야 한다.
   */
  const stopBarInteraction = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const { preview, isDragging, startDrag, clearPreview, consumePointerHandled } = useBarDrag({
    dayColumnWidth: DAY_COLUMN_WIDTH,
    onClick: onClickSchedule,
    onCommit: async ({ schedule, startDate, endDate }) => {
      try {
        await onMovePeriod({ schedule, startDate, endDate });
      } catch {
        // 실패 메시지는 SuperFetchV2 인터셉터가 에러 모달로 띄운다.
        // 여기서 삼키지 않으면 이벤트 핸들러 경계라 처리되지 않은 rejection 이 된다.
      } finally {
        // 성공이면 재조회 결과가 들어오고, 실패면 원래 기간으로 돌아간다
        clearPreview();
      }
    },
  });

  /**
   * 드래그 중에는 카드를 띄우지 않는다.
   *
   * 호출처(막대·미달 띠)마다 검사하지 않고 여기서 한 번에 막는다.
   */
  const showHoverCard = (event: React.SyntheticEvent<HTMLElement>, content: React.ReactNode) => {
    if (isDragging) {
      return;
    }

    hoverCardRef.current?.show(event.currentTarget.getBoundingClientRect(), content);
  };

  const hideHoverCard = useCallback(() => hoverCardRef.current?.hide(), []);

  /**
   * 드래그를 시작할 때 **이미 떠 있는 카드를 내린다.**
   *
   * 카드는 enter 시점 좌표로 `position: fixed` 에 고정돼 막대를 따라오지 않는다. 그래서 그냥 두면
   * 포인터가 막대를 벗어날 때까지(가운데를 잡고 수평으로만 끌면 꽤 오래) 옮기기 전 정보를 담은
   * 낡은 카드가 제자리에 남는다. antd `Tooltip` 시절 `open={isPreview ? false : undefined}` 가
   * 하던 역할이다. 리사이즈 핸들은 `stopPropagation` 으로 막대까지 올라오지 않으므로 각각 감싼다.
   */
  const startDragAndHideCard = (schedule: PromotionSchedule, mode: DragMode) => (event: React.PointerEvent) => {
    hideHoverCard();
    startDrag(schedule, mode)(event);
  };

  /**
   * 목록이 갱신되면 카드를 내린다.
   *
   * hover 중 refetch 로 막대가 unmount 되면 `onMouseLeave` 가 오지 않아,
   * 사라진 항목의 정보를 담은 카드가 `position: fixed` 로 화면에 남는다.
   */
  useEffect(() => {
    hideHoverCard();
  }, [schedules, hideHoverCard]);

  const holidayMap = useMemo(() => new Map(holidays.map((holiday) => [holiday.date, holiday.memo])), [holidays]);

  const holidayDates = useMemo(() => new Set(holidays.map((holiday) => holiday.date)), [holidays]);

  const trackGroups = useMemo(() => groupByTrack(schedules, TRACK_ORDER), [schedules]);

  const dayList = useMemo(() => weeks.flatMap((week) => getWeekDates(week.startDate)), [weeks]);

  /**
   * 날짜 → `dayList` 인덱스.
   *
   * 경고 라인 렌더는 `useMemo` 밖이라 **스크롤마다 다시 실행**된다. 그 안에서 `indexOf` 로 찾으면
   * 트랙 6개 × 미달 주 최대 53개마다 371개를 선형 탐색해 프레임당 수만 회 문자열 비교가 된다 —
   * 가상화로 줄인 비용을 다시 만드는 셈이라 Map 으로 한 번만 만든다.
   */
  const dayIndexMap = useMemo(() => new Map(dayList.map((date, index) => [date, index])), [dayList]);

  /** 공휴일이 놓일 열 위치. 트랙마다 다시 훑지 않도록 한 번만 계산한다. */
  const holidayColumns = useMemo(
    () => dayList.map((date, index) => ({ date, column: index + 1 })).filter(({ date }) => holidayDates.has(date)),
    [dayList, holidayDates],
  );

  /**
   * 트랙별 주당 최소 개수 미달 주. **렌더마다 다시 계산하면 안 된다** —
   * 주 × 확정항목 순회 안에서 날짜 비교가 일어나 연 단위 조회에서는 렌더당 수만 회가 된다.
   */
  const shortagesByTrack = useMemo(
    () => new Map(TRACK_ORDER.map((track) => [track, findWeeklyShortages(track, schedules, weeks)])),
    [schedules, weeks],
  );

  /**
   * 가로축 가상화 — 보이는 열 + 버퍼만 그린다.
   *
   * 연 단위는 371열이고 트랙 6개가 각각 그 그리드를 갖는다. 전부 그리면 그리드 정의와 자식 노드가
   * 함께 커져 레이아웃·삽입 비용이 급증한다(프로파일에서 스크립트 시간의 90% 이상이 `insertBefore`).
   *
   * ⚠️ 훅은 조기 반환(`!range`)보다 **앞에** 있어야 한다 — 호출 순서가 렌더마다 같아야 하므로.
   * 그래서 열 수는 `range` 가 없을 때 0 으로 둔다.
   */
  const totalDays = range?.totalDays ?? 0;

  /**
   * 조회 구간에 잘린 뒤 **실제로 그려지는 일수.**
   *
   * 레인 높이(rowHeights)와 1행 판정이 서로 다른 기준을 쓰면 어긋난다 — 전체 기간으로
   * 높이를 잡아두고 잘린 폭으로 1행이 되면, 목록은 안 그려지는데 레인만 커진 채
   * 빈 공간이 남는다. 드래그 중에는 preview 기간으로 그려지므로 그쪽도 같이 본다.
   *
   * 가로 가상화로 화면 밖에 나간 부분까지는 반영하지 않는다 — 그건 스크롤마다 바뀌는 값이라
   * 레인 높이에 넣으면 스크롤할 때 행 높이가 출렁인다.
   */
  const getVisibleDayCount = useCallback(
    (startDate: string, endDate: string): number => {
      if (!range) {
        return 0;
      }

      const startColumn = getDayIndex(range.start, startDate) + 1;
      const endColumn = getDayIndex(range.start, endDate) + 1;

      return Math.min(endColumn, totalDays) - Math.max(startColumn, 1) + 1;
    },
    [range, totalDays],
  );

  const { scrollRef, startIndex, visibleCount, offsetPx } = useVirtualColumns({
    totalColumns: totalDays,
    columnWidth: DAY_COLUMN_WIDTH,
    offsetWidth: TRACK_LABEL_WIDTH,
    // 스크롤 중 막대가 DOM 에서 제거되면 onMouseLeave 가 오지 않아 카드가 남는다
    onScroll: () => hoverCardRef.current?.hide(),
  });

  const endIndex = startIndex + visibleCount;

  /**
   * 스크롤마다 바뀌는 그리드 설정. **인라인 style 로 넘긴다** —
   * styled-components props 로 두면 값이 바뀔 때마다 클래스가 재생성돼 스크롤이 얼어붙는다.
   */
  const gridStyle = useMemo<React.CSSProperties>(
    () => ({
      // 첫 커밋과 조회 구간 변경 직후에는 0 이다 — `repeat(0, …)` 은 무효 CSS 라 최소 1 로 둔다
      gridTemplateColumns: `repeat(${Math.max(visibleCount, 1)}, ${DAY_COLUMN_WIDTH}px)`,
      marginLeft: offsetPx,
    }),
    [visibleCount, offsetPx],
  );

  /**
   * 밴드 그리드 — 배경(밴드 경계선·격자선·주말 띠) 원점을 밀린 만큼 되돌려야 토·일 위치가 유지된다.
   *
   * ⚠️ **레이어 수와 값의 개수가 맞아야 한다.** `background-image` 는 두 겹이다(격자선 · 주말 띠).
   * 개수가 어긋나면 브라우저가 앞에서부터 순환 적용해 **띠가 엉뚱한 요일에 찍힌다.**
   */
  const bandGridStyle = useMemo<React.CSSProperties>(
    () => ({
      ...gridStyle,
      backgroundPosition: `${-offsetPx}px 0, ${-offsetPx}px 0`,
    }),
    [gridStyle, offsetPx],
  );

  /** 보이는 구간의 일자만. 그리드가 순서대로 채우므로 열 번호는 따로 들고 다니지 않는다. */
  const visibleDays = useMemo(() => dayList.slice(startIndex, endIndex), [dayList, startIndex, endIndex]);

  /**
   * 보이는 구간과 겹치는 주차 헤더. 주 시작이 구간 앞이면 헤더가 왼쪽으로 잘리는데,
   * 잘린 만큼 span 을 줄여 첫 열이 밀리지 않게 한다.
   */
  const visibleWeeks = useMemo(
    () =>
      weeks
        .map((week, index) => {
          const weekStart = index * 7;
          const from = Math.max(weekStart, startIndex);
          const to = Math.min(weekStart + 7, endIndex);

          return { week, span: to - from };
        })
        .filter(({ span }) => span > 0),
    [weeks, startIndex, endIndex],
  );

  /** 보이는 구간의 공휴일만 */
  const visibleHolidayColumns = useMemo(
    () =>
      holidayColumns
        .filter(({ column }) => column > startIndex && column <= endIndex)
        .map(({ date, column }) => ({ date, column: column - startIndex })),
    [holidayColumns, startIndex, endIndex],
  );

  /**
   * 보이는 구간에서 오늘이 몇 번째 열인지. 조회 기간 밖이면 `null`.
   * 공휴일 띠와 같은 방식으로 가상화 오프셋을 뺀다.
   */
  const todayColumn = useMemo(() => {
    const index = dayList.indexOf(today);

    if (index < 0) {
      return null;
    }

    const column = index + 1;

    return column > startIndex && column <= endIndex ? column - startIndex : null;
  }, [dayList, today, startIndex, endIndex]);

  if (!range) {
    return <StyledEmpty>조회 기간을 선택해주세요.</StyledEmpty>;
  }

  const { start: rangeStart } = range;

  /**
   * **공휴일** 세로 띠만 노드로 그린다 — 토·일은 7일 주기라 `StyledBandGrid` 의 배경이 담당한다.
   *
   * 노드로 그리던 시절 연 단위 조회에서 600개가 넘었고, 그 삽입이 React 커밋의 `insertBefore`
   * 비용으로 그대로 나타났다(프로파일 실측: 스크립트 시간의 90% 이상). 공휴일은 날짜가 불규칙해
   * 배경으로 못 그리지만 연간 15개 안팎이라 노드로 둬도 부담이 없다.
   */
  const renderHolidayStripes = (rowCount: number) =>
    visibleHolidayColumns.map(({ date, column }) => (
      <StyledDayStripe
        key={date}
        $stripe={DAY_TYPE_COLOR[DAY_TYPE.HOLIDAY].stripe}
        style={{ gridColumn: `${column} / span 1`, gridRow: `1 / span ${rowCount}` }}
      />
    ));

  /**
   * 트랙별 주당 최소 개수 미달 주에 경고 띠를 깐다.
   *
   * 주 시작일로 열 위치를 잡는다 — `dayList` 는 `weeks[]` 를 펼친 것이라 주 시작일이 반드시 들어 있고,
   * 한 주는 7열이다. 마지막 주가 조회 구간에서 잘리는 경우를 대비해 span 을 남은 열 수로 제한한다.
   */
  const renderShortageBands = (shortages: WeeklyShortage[], rowCount: number) =>
    shortages.map((shortage) => {
      const weekColumn = dayIndexMap.get(shortage.weekStartDate) ?? -1;

      if (weekColumn < 0) {
        return null;
      }

      // 보이는 구간과 겹치는 부분만 그린다
      const from = Math.max(weekColumn, startIndex);
      const to = Math.min(weekColumn + 7, endIndex);

      if (to <= from) {
        return null;
      }

      return (
        <StyledShortageBand
          key={shortage.weekStartDate}
          style={{
            gridColumn: `${from - startIndex + 1} / span ${to - from}`,
            gridRow: `1 / span ${rowCount}`,
          }}
          onMouseEnter={(event) =>
            showHoverCard(
              event,
              `${shortage.weekLabel} (${shortage.weekStartDate} ~ ${shortage.weekEndDate}) · 확정 ${shortage.count}건 — 운영 기준 ${shortage.required}건 이상`,
            )
          }
          onMouseLeave={hideHoverCard}
        />
      );
    });

  return (
    <StyledGanttWrap ref={scrollRef}>
      <ScheduleHoverCard ref={hoverCardRef} />
      <StyledGanttInner $totalDays={totalDays}>
        {/* 주차 헤더 — 한 주가 7열 */}
        <StyledRow>
          <StyledHeaderCell />
          <StyledGrid style={gridStyle}>
            {visibleWeeks.map(({ week, span }) => (
              <StyledWeekHeader key={week.startDate} style={{ gridColumn: `span ${span}` }}>
                {week.label}
              </StyledWeekHeader>
            ))}
          </StyledGrid>
        </StyledRow>

        {/* 일자 헤더 */}
        <StyledRow>
          <StyledHeaderCell />
          <StyledGrid style={gridStyle}>
            {visibleDays.map((date) => {
              const holidayMemo = holidayMap.get(date);
              const dayTypeColor = DAY_TYPE_COLOR[getDayType(date, holidayDates)];
              const dayCell = (
                <StyledDayHeader
                  key={date}
                  $color={dayTypeColor.headerText}
                  $background={dayTypeColor.background}
                  $isToday={date === today}
                >
                  <span className="day-number">{dayjs(date).date()}</span>
                  <span className="day-weekday">{getWeekdayLabel(date)}</span>
                </StyledDayHeader>
              );

              return holidayMemo ? (
                <Tooltip key={date} title={holidayMemo}>
                  {dayCell}
                </Tooltip>
              ) : (
                dayCell
              );
            })}
          </StyledGrid>
        </StyledRow>

        {/* 트랙 밴드 */}
        {trackGroups.map(({ track, rows }) => {
          const meta = TRACK_META[track];
          const rowCount = Math.max(rows.length, 1);
          /**
           * 레인별 높이 — 쿠폰을 펼친 막대가 있으면 그 레인만 키운다.
           *
           * 한 레인에 펼친 막대가 둘 이상일 수 있으므로 **가장 많이 필요한 쪽**에 맞춘다.
           * 접으면 즉시 원래 높이로 돌아온다.
           */
          const rowHeights = Array.from({ length: rowCount }, (_, index) => {
            const extra = (rows[index] ?? []).reduce((max, schedule) => {
              if (!expandedCouponIds.has(schedule.id)) {
                return max;
              }

              /**
               * 1행으로 접힐 막대는 목록을 그리지 않으므로 높이도 키우지 않는다.
               *
               * 판정은 **조회 구간에 잘린 폭**으로 한다(getVisibleDayCount) — 전체 기간으로
               * 재면, 10일짜리를 펼쳐 둔 채 구간을 좁혀 3일만 보이게 했을 때 목록은 사라지는데
               * 레인은 계속 자리를 예약해 빈 블록이 남는다.
               */
              const isPreviewed = preview?.scheduleId === schedule.id;
              const shownStart = isPreviewed ? preview.startDate : schedule.startDate;
              const shownEnd = isPreviewed ? preview.endDate : schedule.endDate;

              if (getVisibleDayCount(shownStart, shownEnd) * DAY_COLUMN_WIDTH < BAR_TWO_ROW_MIN_WIDTH) {
                return max;
              }

              const count = schedule.coupons?.length ?? 0;

              return Math.max(max, count * COUPON_ROW_HEIGHT + COUPON_LIST_PADDING);
            }, 0);

            return BAR_HEIGHT + extra;
          });
          // 조건·툴팁·배지가 같은 데이터를 본다는 걸 드러내려고 한 번만 꺼낸다
          const shortages = shortagesByTrack.get(track) ?? [];
          const isCollapsed = collapsedTracks.has(track);
          const scheduleCount = rows.reduce((sum, row) => sum + row.length, 0);

          return (
            <StyledRow key={track}>
              <StyledTrackLabel $color={meta.color}>
                <StyledTrackToggle
                  type="button"
                  $collapsed={isCollapsed}
                  aria-expanded={!isCollapsed}
                  aria-label={`${meta.label} ${isCollapsed ? '펼치기' : '접기'}`}
                  title={isCollapsed ? '펼치기' : '접기'}
                  onClick={() => toggleTrack(track)}
                >
                  ▼
                </StyledTrackToggle>
                <span className="track-name">{meta.label}</span>
                {meta.description && <span className="track-rule">{meta.description}</span>}
                {/* 띠는 해당 주 구간에만 보이므로, 라벨에 요약을 둬서 스크롤·가로 이동 없이도 알 수 있게 한다 */}
                {shortages.length > 0 && (
                  <Tooltip
                    title={`운영 기준 미달 — ${shortages
                      .map((shortage) => `${shortage.weekLabel} ${shortage.count}/${shortage.required}`)
                      .join(' · ')}`}
                  >
                    <span className="track-shortage">미달 {shortages.length}주</span>
                  </Tooltip>
                )}
              </StyledTrackLabel>

              {/*
                항목이 0건이어도 그리드를 렌더한다 — 빈 밴드로 갈아치우면 **가장 경고가 필요한
                트랙(한 건도 없는 트랙)에서 미달 표시가 사라진다.** 규칙 없는 트랙만 빈 밴드로 둔다.
              */}
              {isCollapsed ? (
                /* 접어도 몇 건이 숨어 있는지는 알려준다 — 빈 트랙과 구분되어야 한다 */
                <StyledCollapsedBand>
                  {scheduleCount > 0 ? `${scheduleCount}건 접힘` : '항목 없음'}
                  {shortages.length > 0 && ` · 미달 ${shortages.length}주`}
                </StyledCollapsedBand>
              ) : (
                <StyledBandGrid $rowHeights={rowHeights} style={bandGridStyle}>
                  {todayColumn !== null && (
                    <StyledTodayStripe
                      style={{ gridColumn: `${todayColumn} / span 1`, gridRow: `1 / span ${rowCount}` }}
                    />
                  )}
                  {renderHolidayStripes(rowCount)}
                  {renderShortageBands(shortages, rowCount)}

                  {/*
                    항목이 없어도 **그리드를 그대로 쓴다.** 별도 빈 밴드로 갈아치우면
                    격자선·주말 띠·공휴일 띠가 그 트랙에서만 끊겨, 세로로 훑을 때
                    날짜 축이 깨진 것처럼 보인다. 안내 문구만 겹쳐 둔다.
                  */}
                  {rows.length === 0 && <StyledBandPlaceholder>—</StyledBandPlaceholder>}

                  {rows.map((row, rowIndex) =>
                    row.map((schedule) => {
                      const statusStyle = STATUS_STYLE[schedule.status];
                      const isPreview = preview?.scheduleId === schedule.id;
                      // 드래그 중이면 임시 기간으로 그린다 (서버 왕복 동안에도 유지)
                      const shownStart = isPreview ? preview.startDate : schedule.startDate;
                      const shownEnd = isPreview ? preview.endDate : schedule.endDate;

                      const startColumn = getDayIndex(rangeStart, shownStart) + 1;
                      const endColumn = getDayIndex(rangeStart, shownEnd) + 1;
                      // 조회 구간 밖으로 삐져나간 부분은 잘라서 그린다
                      const clippedStart = Math.max(startColumn, 1);
                      const clippedEnd = Math.min(endColumn, totalDays);
                      const isClipped = startColumn < 1 || endColumn > totalDays;

                      /**
                       * 가상화 — 보이는 열 구간과 겹치지 않는 막대는 그리지 않는다.
                       * (`startIndex` 는 0-based, 열 번호는 1-based 라 +1 로 맞춘다)
                       *
                       * ⚠️ **드래그 중인 막대는 예외**다. 빠르게 끌면 preview 기간이 버퍼 밖으로 나갈 수 있는데,
                       * 그때 막대가 사라지면 무엇을 옮기는지 안 보인 채로 저장된다. 아래 clamp 덕에
                       * 구간 밖으로 나가도 **그리는 구간(보이는 열 + 버퍼)의 경계**에 붙어 보인다 —
                       * 뷰포트 경계가 아니므로 많이 스크롤한 상태면 화면 밖일 수 있다.
                       */
                      if (!isPreview && (clippedEnd < startIndex + 1 || clippedStart > endIndex)) {
                        return null;
                      }

                      /**
                       * 보이는 구간 기준 상대 열 위치로 되돌린다.
                       *
                       * 양쪽 모두 clamp 해야 한다 — 위 예외로 살아남은 preview 가 그리는 구간 오른쪽 밖이면
                       * `visibleStart` 가 정의에 없는 열 번호가 되고, CSS Grid 는 그 자리에 암시적 트랙을 만든다.
                       * 전체 스크롤 폭 유지 때문에 남아 있던 폭이 그 트랙들에 균등 분배되어
                       * 막대가 타임라인 오른쪽에 수백 px 폭 덩어리로 그려진다.
                       */
                      const visibleStart = Math.min(Math.max(clippedStart - startIndex, 1), Math.max(visibleCount, 1));
                      const visibleEnd = Math.min(clippedEnd - startIndex, visibleCount);

                      /**
                       * 잘린 막대는 드래그를 막는다 — 보이는 폭과 실제 기간이 달라
                       * 사용자가 의도한 만큼 옮겨지지 않는다. 조회 기간을 넓히면 드래그할 수 있다.
                       */
                      const isDraggable = canUpdate && !isClipped;

                      const span = Math.max(visibleEnd - visibleStart + 1, 1);
                      /**
                       * 2행을 유지할지 1행으로 접을지는 **일수가 아니라 폭**으로 판정한다.
                       * 열 너비가 바뀌면 같은 일수라도 읽히는 정도가 달라진다.
                       */
                      const isCompact = span * DAY_COLUMN_WIDTH < BAR_TWO_ROW_MIN_WIDTH;
                      const scheduleMeta = extractScheduleMeta(schedule.memo);
                      // 연결된 쿠폰 우선, 없으면 memo 의 계획 표기
                      const couponLabel = getCouponLabel(schedule);
                      // 혜택 큰 순 — 서버는 id 순이라 그대로 쓰면 가장 작은 혜택이 앞에 온다
                      const sortedCoupons = sortCouponsByBenefit(schedule.coupons ?? []);
                      const isExpanded = expandedCouponIds.has(schedule.id);
                      // 종일이면 null — 타임딜처럼 시간 단위로 진행하는 항목만 시각을 노출한다
                      const timeRangeLabel = getTimeRangeLabel(shownStart, shownEnd);
                      /**
                       * 메타 줄에 무엇을 얼마나 담을지 — **폭 예산을 우선순위대로 배분한다.**
                       *
                       * 넘치면 `overflow: hidden` 에 잘려 `#` 한 글자만 남는 꼴이 된다(4일 막대에서 실제로 났다).
                       * 그래서 들어갈 만큼만 그린다. 우선순위는 이 화면의 용도에서 나온다 —
                       * **기획전 연결 > 쿠폰 > 출처·유형**. 앞의 둘은 "준비가 됐나" 를 훑는 축이고
                       * 출처는 참고 정보라 hover 카드가 대신할 수 있다.
                       */
                      const metaSpace = span * DAY_COLUMN_WIDTH - BAR_META_RESERVED;
                      /**
                       * 시각이 가장 먼저다.
                       *
                       * 종일이면 null 이라 예산을 쓰지 않는다. 시각이 붙는 항목은 타임딜처럼
                       * **몇 시에 여는지가 곧 그 항목의 정체**라, 기획전·쿠폰보다 앞에 둔다.
                       */
                      const spaceAfterTime = metaSpace - (timeRangeLabel ? TIME_BADGE_WIDTH : 0);
                      const spaceAfterEvent = spaceAfterTime - (schedule.event ? EVENT_BADGE_WIDTH : 0);
                      /**
                       * 좁으면 칩에서 id 를 뺀다.
                       *
                       * `#5329 48%` 는 `48%` 의 두 배 폭이다. 폭이 없을 때 id 를 고집하면
                       * 칩이 한 개도 못 들어가 **혜택 자체가 안 보인다** — 그건 더 큰 손실이다.
                       */
                      const showChipId = spaceAfterEvent >= CHIP_WIDTH_WITH_ID;
                      const chipWidth = showChipId ? CHIP_WIDTH_WITH_ID : CHIP_WIDTH_PLAIN;
                      /**
                       * ⚠️ **최소 1개를 강제하지 않는다.**
                       *
                       * 공간이 없는데도 칩을 그리면 넘쳐서 잘린다 — 4일 막대에서 `#` 한 글자만 남는
                       * 꼴이 실제로 났다. 한 개도 못 넣을 때는 `+n` 만 띄워 **몇 개가 있는지**라도 알린다.
                       */
                      /**
                       * ⚠️ **0 미만으로 내려가면 안 된다.**
                       *
                       * 시각(68) + 기획전(58) 이 함께 붙는 4일 막대에서 예산이 `-32` 가 된다.
                       * 그대로 두면 `chipCount` 가 음수가 되고 `slice(0, -1)` 이 되어
                       * **칩이 되려 거의 다 그려지며**(마지막 하나만 빠진다) `+n` 도 1 크게 나온다.
                       */
                      const chipBudget = Math.max(Math.floor(spaceAfterEvent / chipWidth), 0);
                      const chipCount = Math.min(sortedCoupons.length, chipBudget);
                      const spaceAfterChips = spaceAfterEvent - chipCount * chipWidth;
                      /**
                       * 기획전 **이름**은 쿠폰보다 뒤다.
                       *
                       * 연결 여부는 `#id` 로 이미 드러나고, 이름은 "어느 기획전인지" 를 돕는 부가 정보다.
                       * 쿠폰 혜택은 값 자체가 정보라 먼저 자리를 준다 — 이름을 앞에 두면
                       * 5일 막대에서 칩이 한 개도 못 들어간다.
                       */
                      const showEventName = schedule.event !== null && spaceAfterChips >= EVENT_NAME_WIDTH;
                      // 그러고도 남으면 출처·유형
                      const showPlainMeta =
                        spaceAfterChips - (showEventName ? EVENT_NAME_WIDTH : 0) >= PLAIN_META_WIDTH;
                      /**
                       * 카드에는 **막대에 보이는 기간**을 넘긴다.
                       *
                       * 드래그를 놓으면 `isDragging` 은 곧 false 가 되지만 `preview` 는 서버 응답까지
                       * 유지된다(막대가 원위치로 튀지 않게 — `useBarDrag` 의 `handleUp`). 그 사이 hover 하면
                       * 막대는 새 기간, 카드는 원본 기간을 보여 **같은 항목이 서로 다른 기간을 말한다.**
                       * 카드를 감추는 대신 기간을 맞춰, 저장을 기다리는 동안에도 옮긴 결과를 그대로 읽게 한다.
                       */
                      const shownSchedule = isPreview
                        ? { ...schedule, startDate: shownStart, endDate: shownEnd }
                        : schedule;

                      const hasMetaRow =
                        !isCompact &&
                        (timeRangeLabel !== null ||
                          couponLabel !== null ||
                          schedule.event !== null ||
                          scheduleMeta.sourceLabels.length > 0 ||
                          schedule.type !== null);

                      return (
                        <StyledBar
                          key={schedule.id}
                          role="button"
                          tabIndex={0}
                          style={{
                            gridColumn: `${visibleStart} / span ${span}`,
                            gridRow: `${rowIndex + 1} / span 1`,
                          }}
                          $tone={statusStyle.isGray ? GRAY_BAR_TONE : meta.barTone}
                          $opacity={statusStyle.opacity}
                          $isCompact={isCompact}
                          $isDraggable={isDraggable}
                          $isPreview={isPreview}
                          onMouseEnter={(event) =>
                            showHoverCard(event, <ScheduleTooltip schedule={shownSchedule} isClipped={isClipped} />)
                          }
                          onMouseLeave={hideHoverCard}
                          onFocus={(event) =>
                            showHoverCard(event, <ScheduleTooltip schedule={shownSchedule} isClipped={isClipped} />)
                          }
                          onBlur={hideHoverCard}
                          onPointerDown={isDraggable ? startDragAndHideCard(schedule, DRAG_MODE.MOVE) : undefined}
                          onClick={() => {
                            /**
                             * 마우스 경로는 pointer 이벤트(useBarDrag)가 전담하므로 그 직후 오는
                             * click 은 흘린다. 키보드 Enter·Space 는 pointer 이벤트 없이 click 만
                             * 만들어 플래그가 false 이므로 여기서 모달이 열린다.
                             *
                             * ⚠️ `isDraggable` 로 판정하면 안 된다 — 이 값은 preview 가 반영된
                             * 기간으로 계산되므로, 구간 경계 밖으로 끌어 놓으면 커밋 직후 리렌더에서
                             * false 로 뒤집힌다. 그 상태의 click 이 통과하면 드래그 이전 스냅샷을
                             * 담은 모달이 열리고, 저장 시 방금 옮긴 기간이 되돌아간다.
                             */
                            if (consumePointerHandled()) {
                              return;
                            }

                            onClickSchedule(schedule);
                          }}
                          /**
                           * div 라서 Enter·Space 가 click 으로 바뀌지 않는다 — 직접 연결한다.
                           * 안쪽 링크·토글에서 올라온 키 입력은 무시해야 모달이 같이 열리지 않는다.
                           */
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) {
                              return;
                            }

                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onClickSchedule(schedule);
                            }
                          }}
                        >
                          {isDraggable && (
                            <>
                              <span
                                className="resize-handle resize-handle-start"
                                onPointerDown={startDragAndHideCard(schedule, DRAG_MODE.RESIZE_START)}
                              />
                              <span
                                className="resize-handle resize-handle-end"
                                onPointerDown={startDragAndHideCard(schedule, DRAG_MODE.RESIZE_END)}
                              />
                            </>
                          )}
                          <span className="bar-main">
                            {!isCompact && schedule.adminName && (
                              <span className="bar-manager">{schedule.adminName}</span>
                            )}
                            <span className="bar-title">{schedule.title}</span>
                            {/*
                              1행으로 접힌 막대(3일 이하, 실측 28%)는 메타 줄이 없어
                              **연결이 있는지조차 알 수 없다.** 제목 뒤에 최소 표식만 남겨
                              "여기 뭔가 더 있다" 를 알리고 자세한 건 hover 카드로 넘긴다.
                            */}
                            {isCompact && schedule.event && (
                              <LinkOutlined className="bar-flag bar-flag-event" title="기획전 연결됨" />
                            )}
                            {isCompact && sortedCoupons.length > 0 && (
                              <span className="bar-flag bar-flag-coupon" title={`쿠폰 ${sortedCoupons.length}개`} />
                            )}
                          </span>
                          {hasMetaRow && (
                            <span className="bar-meta">
                              {/* 시각 — 폭 예산에서 가장 먼저 자리를 받았으므로 조건 없이 그린다 */}
                              {timeRangeLabel && <span className="bar-time">{timeRangeLabel}</span>}
                              {/*
                                기획전 연결 — 연결된 것만 그린다. dev 실측에서 연결은 0.2% 뿐이라
                                "없음"까지 표시하면 화면 대부분이 경고가 되어 아무 정보도 아니게 된다.
                              */}
                              {schedule.event && (
                                <a
                                  className="bar-event"
                                  href={setDetailPageUrl(EVENT_EDIT_URL_PATH, schedule.event.id)}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={schedule.event.mainTitle}
                                  onClick={stopBarInteraction}
                                  onPointerDown={stopBarInteraction}
                                >
                                  <LinkOutlined />#{schedule.event.id}
                                  {showEventName && <span className="ev-name">{schedule.event.mainTitle}</span>}
                                </a>
                              )}
                              {sortedCoupons.length > 0 ? (
                                /*
                                  쿠폰 — 혜택 칩을 큰 순으로 나열한다. `15% 외 3` 처럼 접으면
                                  등급 구조(1/2/4/8%)가 안 보이고, 대표로 뽑히는 첫 번째가
                                  하필 가장 작은 혜택인 경우가 많다.
                                */
                                <button
                                  type="button"
                                  className="bar-coupons"
                                  aria-expanded={isExpanded}
                                  title={isExpanded ? '쿠폰 접기' : '쿠폰 펼쳐 보기'}
                                  onClick={(event) => {
                                    stopBarInteraction(event);
                                    toggleCouponList(schedule.id);
                                  }}
                                  onPointerDown={stopBarInteraction}
                                >
                                  {sortedCoupons.slice(0, chipCount).map((coupon) => (
                                    <span key={coupon.id} className="bar-chip" title={`#${coupon.id} ${coupon.name}`}>
                                      {showChipId && <em>#{coupon.id}</em>}
                                      {formatCouponBenefit(coupon)}
                                    </span>
                                  ))}
                                  {sortedCoupons.length > chipCount && (
                                    <span className="bar-chip-more">+{sortedCoupons.length - chipCount}</span>
                                  )}
                                </button>
                              ) : (
                                /* 연결 전 계획 표기(memo 파싱)는 점선 칩으로 — 아직 실물이 없다는 뜻 */
                                couponLabel && <span className="bar-chip bar-chip-planned">{couponLabel}</span>
                              )}
                              {showPlainMeta && (
                                <>
                                  {scheduleMeta.sourceLabels.map((label) => (
                                    <span key={label} className="bar-plain">
                                      {label}
                                    </span>
                                  ))}
                                  {schedule.type && <span className="bar-plain">{TYPE_LABEL[schedule.type]}</span>}
                                </>
                              )}
                            </span>
                          )}
                          {/*
                            1행으로 접힌 막대는 **펼침을 무시한다.**

                            메타 한 줄도 못 담는 폭에 목록을 그리면 글자가 잘려 읽히지도 않는다.
                            상태(expandedCouponIds)는 지운다 — 드래그로 잠깐 줄였다 되돌리면
                            펼친 상태가 그대로 살아나야 하므로, 여기서는 그리기만 건너뛴다.
                          */}
                          {isExpanded && !isCompact && sortedCoupons.length > 0 && (
                            <span className="bar-coupon-list">
                              {sortedCoupons.map((coupon) => (
                                <span key={coupon.id} className="bar-coupon-item">
                                  <span className="amt">{formatCouponBenefit(coupon)}</span>
                                  <a
                                    className="bar-coupon-link"
                                    href={setDetailPageUrl(COUPON_DETAIL_URL_PATH, coupon.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={stopBarInteraction}
                                    onPointerDown={stopBarInteraction}
                                  >
                                    #{coupon.id}
                                  </a>
                                  <span className="nm">{coupon.name}</span>
                                  {coupon.startDate && coupon.endDate && (
                                    <span className="pd">
                                      {toDatePart(coupon.startDate)} ~ {toDatePart(coupon.endDate)}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </span>
                          )}
                        </StyledBar>
                      );
                    }),
                  )}
                </StyledBandGrid>
              )}
            </StyledRow>
          );
        })}
      </StyledGanttInner>
    </StyledGanttWrap>
  );
};
